import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useBusinessStore } from '../store/useBusinessStore';
import toast from 'react-hot-toast';
import { buildManualOrderRow, isMissingColumnError, orderView, orderNumber } from '../utils/orders';

export const useOrders = () => {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  const activeBusiness = masterDb.find(b => String(b.id) === String(activeBusinessId));
  const orders = activeBusiness?.orders || [];
  
  const [isProcessing, setIsProcessing] = useState(false);

  // Update Order Status
  const updateOrderStatus = async (orderId, newStatus, extraFields = {}) => {
    const payload = { status: newStatus, ...extraFields };
    
    // Optimistic Update Local
    setMasterDb(prev => prev.map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          orders: (b.orders || []).map(o => String(o.id) === String(orderId) ? { ...o, ...payload } : o)
        };
      }
      return b;
    }));

    try {
      let { error } = await supabase.from('orders').update(payload).eq('id', orderId);
      // `is_paid` la crea la migración 20261007g: sin ella el estado se guarda igual y solo falta la marca de pago.
      if (isMissingColumnError(error, 'is_paid') && 'is_paid' in payload) {
        const { is_paid: _omit, ...withoutPaid } = payload;
        ({ error } = await supabase.from('orders').update(withoutPaid).eq('id', orderId));
        if (!error) {
          toast("Estado guardado. Para registrar los pagos ejecuta la migración 20261007g_orders_is_paid.sql en Supabase.", { icon: '⚠️' });
          return;
        }
      }
      if (error) {
        console.warn("[Supabase] error updating order status:", error.message);
        toast.error("Error al sincronizar estado: " + error.message);
      } else {
        toast.success("Estado del pedido actualizado.");
      }
    } catch (err) {
      console.error(err);
      toast("Estado del pedido guardado localmente (Sin conexión).", { icon: '⚠️' });
    }
  };

  // Pago recibido: independiente del estado (una transferencia puede llegar antes de la entrega).
  const setOrderPaid = async (orderId, paid) => {
    const apply = (value) => setMasterDb(prev => prev.map(b => (
      String(b.id) === String(activeBusinessId)
        ? { ...b, orders: (b.orders || []).map(o => String(o.id) === String(orderId) ? { ...o, is_paid: value } : o) }
        : b
    )));
    const previous = orders.find(o => String(o.id) === String(orderId))?.is_paid === true;
    apply(paid);
    try {
      const { error } = await supabase.from('orders').update({ is_paid: paid }).eq('id', orderId);
      if (error) throw error;
      toast.success(paid ? "Pago registrado." : "Pago quitado.");
    } catch (err) {
      apply(previous);
      toast.error(isMissingColumnError(err, 'is_paid')
        ? "Falta ejecutar la migración 20261007g_orders_is_paid.sql en Supabase."
        : "No se pudo guardar el pago: " + (err?.message || 'error'));
    }
  };

  // Create Manual Order
  const createManualOrder = async (orderForm, onSuccess) => {
    setIsProcessing(true);
    if (!orderForm.customer_name || (!orderForm.items_text && !(orderForm.lines || []).length)) {
      toast.error("Por favor completa el nombre del cliente y el detalle del pedido");
      setIsProcessing(false);
      return;
    }
    
    const newOrder = buildManualOrderRow(orderForm, activeBusinessId);

    try {
      // `source` (origen «manual») lo agrega la migración 20261006a_orders_source.sql: sin ella el pedido se guarda igual, solo que sin esa marca.
      let { data, error } = await supabase.from('orders').insert([{ ...newOrder, source: 'manual' }]).select('*');
      if (isMissingColumnError(error, 'source')) {
        ({ data, error } = await supabase.from('orders').insert([newOrder]).select('*'));
      }
      if (error) {
        toast.error("Error al guardar pedido manual: " + error.message);
      } else {
        toast.success("Pedido manual registrado exitosamente");
        // Optimistic append
        if (data && data[0]) {
           setMasterDb(prev => prev.map(b => {
            if (String(b.id) === String(activeBusinessId)) {
              return { ...b, orders: [data[0], ...(b.orders || [])] };
            }
            return b;
          }));
        }
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      toast.error("Error de red o base de datos al crear pedido");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatOrderItems = (order) => {
    let itemsText = '';
    try {
      const parsedItems = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
      if (Array.isArray(parsedItems)) {
        itemsText = parsedItems.map(i => `- ${i.cantidad || i.qty || i.quantity || 1}x ${i.nombre || i.name || i.producto || i.item || 'Producto'} ($${i.precio || i.price || i.precio_unitario || 0})`).join('\n');
      } else {
        itemsText = order.items;
      }
    } catch (e) {
      itemsText = order.items || 'N/A';
    }
    return itemsText;
  };

  const copyForDelivery = (order) => {
    const itemsText = formatOrderItems(order);
    const v = orderView(order);
    const textToCopy = `*PEDIDO ${orderNumber(order)}*\n\n*Cliente:* ${v.name || 'N/A'}\n*Dirección:* ${v.pickup ? 'Recoge en Local' : (v.address || 'Recoge en Local')}\n*Teléfono:* ${v.phone || 'N/A'}\n\n*Detalle:*\n${itemsText}\n\n*Total a Cobrar:* $${v.total}\n*Estado de Pago:* ${v.paid ? '✅ YA PAGADO' : '❌ PENDIENTE DE COBRO'}`;
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => toast.success("Datos del pedido copiados al portapapeles."))
      .catch(() => toast.error("Error al copiar."));
  };

  return {
    orders,
    activeBusiness,
    isProcessing,
    updateOrderStatus,
    setOrderPaid,
    createManualOrder,
    formatOrderItems,
    copyForDelivery
  };
};
